/**
 * A drawer's comments, docked beside it rather than buried at the bottom of
 * its body. Shared: the transaction, card, payment and invoice drawers, and the
 * contact page, which has no drawer, so the panel docks against the window.
 *
 * Docking puts the panel OUTSIDE the drawer's DOM, so the drawer has to lift
 * its focus trap while this is open (`disableEnforceFocus`) or the composer
 * cannot be typed into. That is the one composition the DS documents the escape
 * hatch for.
 *
 * Below `md` there is no room beside the drawer, so the same panel covers it as
 * a sheet instead. One interaction model, two geometries.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Modal, Paper, Typography, useTheme } from "@mui/material";
import { IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";
import { CommentActions } from "hooks/useComments";
import CommentList from "./CommentList";
import CommentComposer from "./CommentComposer";

interface CommentsPanelProps {
  open: boolean;
  onClose: () => void;
  /**
   * Drawer width in px, so the panel docks flush against its edge. Omit on a
   * page, where it docks against the window's edge instead.
   */
  drawerWidth?: number;
  /** False below `md`, where the panel covers the drawer instead of docking. */
  docked: boolean;
  commentActions: CommentActions;
  openProfileOnMention?: boolean;
}

export const COMMENTS_PANEL_WIDTH = 380;

// The DS drawer's own default width. Mirrored here only because the panel docks
// flush against the paper edge and has to know where it is.
export const DEFAULT_DRAWER_WIDTH = 420;

const CommentsPanel: React.FC<CommentsPanelProps> = ({
  open,
  onClose,
  drawerWidth,
  docked,
  commentActions,
  openProfileOnMention,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!open) return null;

  const onPage = drawerWidth === undefined;
  // Covering a page, the sheet is all that is reachable, so it is a modal.
  const modal = onPage && !docked;
  const edge = "1px solid var(--color-border)";

  const panel = (
    <Paper
      elevation={0}
      role={modal ? "dialog" : undefined}
      aria-modal={modal || undefined}
      aria-label={modal ? t("Comments") : undefined}
      // Between the two MUI layers on purpose: above `zIndex.drawer` (1200), so
      // the drawer's backdrop neither dims the panel nor swallows its clicks,
      // and below `zIndex.modal` (1300), so every dialog opened from the drawer
      // lands on top. Sitting above the modal layer instead meant each new
      // confirmation had to be named in a visibility gate to stay reachable.
      // On a page it sits just below the drawer layer instead, so a drawer the
      // page opens covers it like the rest of the page.
      sx={{
        position: "fixed",
        top: 0,
        bottom: 0,
        right: docked ? (drawerWidth ?? 0) : 0,
        width: docked ? COMMENTS_PANEL_WIDTH : "100%",
        maxWidth: "100%",
        zIndex: onPage ? theme.zIndex.drawer - 1 : theme.zIndex.drawer + 1,
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
        borderInlineEnd: docked && !onPage ? edge : undefined,
        borderInlineStart: docked && onPage ? edge : undefined,
        // The DS Drawer's paper. Elevated is the DS input's hover fill, so on
        // that surface the composer vanished on hover.
        backgroundColor: "var(--color-bg-surface-canvas-solid)",
      }}
    >
      <Inline
        justify="between"
        align="center"
        sx={{
          padding: "var(--space-4) var(--space-5)",
          borderBlockEnd: "1px solid var(--color-border)",
        }}
      >
        <Typography variant="subtitle2">{t("Comments")}</Typography>
        <IconButton
          variant="ghost"
          aria-label={t("Close comments")}
          onClick={onClose}
        >
          <HugeiconsIcon icon={Cancel01Icon} size={18} />
        </IconButton>
      </Inline>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "var(--space-5)",
        }}
      >
        <CommentList
          actions={commentActions}
          openProfileOnMention={openProfileOnMention}
        />
      </Box>

      <Box
        sx={{
          marginInline: "var(--space-5)",
          paddingBlock: "var(--space-4) var(--space-5)",
          borderBlockStart: "1px solid var(--color-border)",
        }}
      >
        <CommentComposer actions={commentActions} />
      </Box>
    </Paper>
  );

  if (!modal) return panel;

  // The modal traps focus, hides the page from assistive tech and restores
  // focus on close. An Escape that only closes the mention picker leaves it open.
  return (
    <Modal
      open
      hideBackdrop
      onClose={() => {
        if (!commentActions.mentionDropdownOpen) onClose();
      }}
      sx={{ zIndex: theme.zIndex.drawer - 1 }}
    >
      {panel}
    </Modal>
  );
};

export default CommentsPanel;
